import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Dropdown, Menu, Modal, Tooltip, Space, Input, Pagination } from 'antd';
import {
    MoreOutlined,
    CopyOutlined,
    FilterOutlined,
    CloudUploadOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckSquareOutlined,
    BorderOutlined,
} from '@ant-design/icons';
import apiCall from '../app/API/apiService';
import apiEndpoints from '../app/API/apiEndpoints';
import { toasty } from '../Helper';
import QuestionFilters from './QuestionFilters';
import AddQuestionDialog from './AddQuestionDialog';
import UploadQuestionDialog from './UploadQuestionDialog';
import './questionListStyle.css';
import { useNavigate } from 'react-router-dom';

function QuestionList() {
    const [questions, setQuestions] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const navigate = useNavigate();
    const [selectionContext, setSelectionContext] = useState(null);
    const [selectionMode, setSelectionMode] = useState('none');
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [roundDialogOpen, setRoundDialogOpen] = useState(false);
    const [selectedRound, setSelectedRound] = useState('');

    useEffect(() => {
        const contextStr = localStorage.getItem('questionSelectionContext');
        if (contextStr) {
            setSelectionContext(JSON.parse(contextStr));
            setSelectionMode('manual');
        } else {
            setSelectionMode('none');
            setSelectedQuestions([]);
        }

        return () => {
            localStorage.removeItem('questionSelectionContext');
        };
    }, []);

    const showSelectionUI = Boolean(selectionContext);

    const handleQuestionSelect = (e, questionId) => {
        e.stopPropagation();
        setSelectedQuestions(prev => {
            if (prev.includes(questionId)) {
                return prev.filter(id => id !== questionId);
            }
            if (selectionContext?.singleSelect) {
                return [questionId];
            }
            return [...prev, questionId];
        });
    };

    const handleUploadQuestions = () => {
        if (selectedQuestions.length === 0) {
            toasty('Please select questions to add', 'warning');
            return;
        }
        setRoundDialogOpen(true);
    };

    const handleRoundSubmit = async () => {
        const roundNumber = parseInt(selectedRound);
        if (!roundNumber || roundNumber < 1) {
            toasty('Please enter a valid round number', 'warning');
            return;
        }

        try {
            const response = await apiCall(
                "post",
                `/api/jobopenings/${selectionContext.jobOpeningId}/rounds/${roundNumber}/append-questions`,
                {
                    questions: selectedQuestions
                }
            );

            if (response) {
                toasty('Questions added successfully', 'success');
                localStorage.removeItem('questionSelectionContext');
                if (selectionContext.returnPath) {
                    navigate(selectionContext.returnPath);
                }
            }
        } catch (error) {
            console.error('Error adding questions:', error);
            toasty('Error adding questions to round', 'error');
        } finally {
            setRoundDialogOpen(false);
        }
    };

    // Define fetchQuestions function
    const fetchQuestions = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await apiCall(
                "get",
                apiEndpoints.QUESTIONS,
                null,
                false
            );
            
            if (response && Array.isArray(response)) {
                const validQuestions = response.filter(q => q && q.id && !q.is_deleted);
                setQuestions(validQuestions);
                setTotalPages(Math.ceil(validQuestions.length / pageSize));
            } else {
                toasty('Failed to fetch questions', 'error');
                setQuestions([]);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            toasty('Error fetching questions', 'error');
            setQuestions([]);
            setTotalPages(1);
        } finally {
            setIsLoading(false);
        }
    }, [pageSize]);

    // Use fetchQuestions in useEffect
    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions, page, pageSize]);

    // Add this function to get paginated data
    const getPaginatedData = () => {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return questions.slice(startIndex, endIndex);
    };

    const handleMenuClick = (event, question) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedQuestion(question);
        console.log('Selected Question:', question);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDelete = async () => {
        if (!selectedQuestion || !selectedQuestion.id) {
            toasty('Question ID not found', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const response = await apiCall(
                "delete",
                `${apiEndpoints.QUESTIONS}/${selectedQuestion.id}`,
                null,
                false
            );
            
            if (response !== undefined || response === null) {
                // Update the UI state by filtering out the deleted question
                setQuestions(prevQuestions => {
                    const updatedQuestions = prevQuestions.filter(
                        question => question.id !== selectedQuestion.id
                    );
                    // Update total pages based on new question count
                    setTotalPages(Math.ceil(updatedQuestions.length / pageSize));
                    return updatedQuestions;
                });
                
                toasty('Question deleted successfully', 'success');
                handleMenuClose();
                setDeleteDialogOpen(false);
                setSelectedQuestion(null);
            } else {
                toasty('Failed to delete question', 'error');
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            toasty(error.response?.data?.detail || 'Error deleting question', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = () => {
        if (!selectedQuestion || !selectedQuestion.id) {
            toasty('Question ID not found', 'error');
            return;
        }
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handlePageChange = (newPage, newPageSize) => {
        setPage(newPage);
        setPageSize(newPageSize);
    };

    const handleRowsPerPageChange = (current, size) => {
        setPageSize(size);
        setPage(1);
    };

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text);
        toasty('Copied to clipboard!', 'success');
    };

    const handleApplyFilters = (filteredData) => {
        setQuestions(filteredData);
        setActiveFilters(true);
        setPage(1);
    };

    const handleClearFilters = async () => {
        try {
            const response = await apiCall("get", apiEndpoints.QUESTIONS);
            if (response) {
                setQuestions(response);
                setActiveFilters(false);
                setPage(1);
            }
        } catch (error) {
            console.error('Error clearing filters:', error);
            toasty('Error clearing filters', 'error');
        }
    };

    const handleAddDialogClose = (success) => {
        setAddDialogOpen(false);
        if (success) {
            fetchQuestions();
        }
    };

    const handleEditClick = async (record) => {
        try {
            setIsLoading(true);
            const response = await apiCall("get",`${apiEndpoints.QUESTIONS}/${record.id}`);
            
            if (response) {
                setSelectedQuestion(response);
                setEditDialogOpen(true);
            } else {
                toasty('Failed to fetch question details', 'error');
            }
        } catch (error) {
            console.error('Error fetching question details:', error);
            toasty('Error fetching question details', 'error');
        } finally {
            setIsLoading(false);
            handleMenuClose();
        }
    };

    const handleEditDialogClose = (success) => {
        setEditDialogOpen(false);
        setSelectedQuestion(null);
        if (success) {
            fetchQuestions();
        }
    };

    const handleUploadDialogClose = (success) => {
        setUploadDialogOpen(false);
        if (success) {
            fetchQuestions();
        }
    };

    const renderArrayOrNA = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0) {
            return 'N/A';
        }
        return arr.join(', ');
    };

    // Update the selection mode handler
    const handleSelectionModeChange = async (mode) => {
        setSelectionMode(mode);
        if (mode === 'all') {
            const allIds = questions.map(q => q.id);
            setSelectedQuestions(allIds);
        } else if (mode === 'none' || mode === 'manual') {
            setSelectedQuestions([]);
        }
    };

    const columns = [
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>ID</span>,
            dataIndex: 'id',
            key: 'id',
            render: (text) => (
                <Space>
                    <span style={{ color: "#27272A", fontSize: 11 }}>
                        {text ? text.slice(-10) : 'N/A'}
                    </span>
                    <Tooltip title="Copy ID">
                        <Button
                            icon={<CopyOutlined />}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(text);
                                toasty('Copied to clipboard!', 'success');
                            }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Question</span>,
            dataIndex: 'question',
            key: 'question',
            render: (text) => (
                <span style={{ color: "#27272A", fontSize: 11 }}>
                    {text || 'N/A'}
                </span>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Difficulty</span>,
            dataIndex: 'difficulty',
            key: 'difficulty',
            render: (text) => (
                <span style={{ color: "#27272A", fontSize: 11 }}>
                    {text || 'N/A'}
                </span>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Type</span>,
            dataIndex: 'type',
            key: 'type',
            render: (text) => (
                <span style={{ color: "#27272A", fontSize: 11 }}>
                    {text || 'N/A'}
                </span>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Tags</span>,
            dataIndex: 'tags',
            key: 'tags',
            render: (tags) => (
                <span style={{ color: "#27272A", fontSize: 11 }}>
                    {renderArrayOrNA(tags)}
                </span>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Skills</span>,
            dataIndex: 'skills',
            key: 'skills',
            render: (skills) => (
                <span style={{ color: "#27272A", fontSize: 11 }}>
                    {renderArrayOrNA(skills)}
                </span>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Job Category</span>,
            dataIndex: 'jobcategory',
            key: 'jobcategory',
            render: (categories) => (
                <span style={{ color: "#27272A", fontSize: 11 }}>
                    {renderArrayOrNA(categories)}
                </span>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Job Subcategory</span>,
            dataIndex: 'jobsubcategory',
            key: 'jobsubcategory',
            render: (subcategories) => (
                <span style={{ color: "#27272A", fontSize: 11 }}>
                    {renderArrayOrNA(subcategories)}
                </span>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Actions</span>,
            key: 'actions',
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item 
                                icon={<EditOutlined />} 
                                onClick={() => handleEditClick(record)}
                            >
                                Edit
                            </Menu.Item>
                            <Menu.Item 
                                icon={<DeleteOutlined />} 
                                onClick={() => {
                                    setSelectedQuestion(record);
                                    setDeleteDialogOpen(true);
                                }}
                                danger
                            >
                                Delete
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                </Dropdown>
            ),
        },
    ];

    if (showSelectionUI) {
        columns.unshift({
            title: '',
            key: 'selection',
            width: 48,
            render: (_, record) => (
                <Button
                    icon={selectedQuestions.includes(record.id) ? 
                        <CheckSquareOutlined /> : 
                        <BorderOutlined />
                    }
                    onClick={(e) => handleQuestionSelect(e, record.id)}
                    type="text"
                />
            ),
        });
    }

    return (
        <div className="question-list-container">
            <div style={{ margin: 10 }}>
                <Space style={{ marginBottom: 16, justifyContent: 'flex-end', width: '100%' }}>
                    {activeFilters && (
                        <Button
                            icon={<FilterOutlined />}
                            onClick={handleClearFilters}
                        >
                            Clear Filters
                        </Button>
                    )}
                    <Button
                        icon={<FilterOutlined />}
                        onClick={() => setFilterDrawerOpen(true)}
                    >
                        Filters
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => setAddDialogOpen(true)}
                    >
                        Add New Question
                    </Button>
                    <Button
                        icon={<CloudUploadOutlined />}
                        onClick={() => setUploadDialogOpen(true)}
                    >
                        Upload Questions
                    </Button>
                    {showSelectionUI && selectedQuestions.length > 0 && (
                        <Button
                            icon={<CloudUploadOutlined />}
                            onClick={handleUploadQuestions}
                        >
                            Upload Selected Questions ({selectedQuestions.length})
                        </Button>
                    )}
                </Space>

                <Table
                    columns={columns}
                    dataSource={getPaginatedData()}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    rowClassName={(record, index) => index % 2 === 0 ? '' : 'custom-row'}
                    footer={() => (
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px',
                            backgroundColor: 'rgba(238, 238, 240, 1)',
                            borderTop: '1px solid #f0f0f0',
                            borderRadius: '0 0 8px 8px'
                        }}>
                            <div style={{ 
                                fontFamily: 'Inter', 
                                fontSize: '12px', 
                                fontWeight: 400, 
                                lineHeight: '18px', 
                                letterSpacing: '-0.1px', 
                                color: '#27272A' 
                            }}>
                                Showing {Math.min(questions.length, pageSize)} of {totalPages * pageSize} Questions
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                {totalPages > 0 && (
                                    <Pagination
                                        current={page}
                                        total={totalPages * pageSize}
                                        pageSize={pageSize}
                                        showSizeChanger={false}
                                        size="small"
                                        onChange={(newPage) => handlePageChange(newPage, pageSize)}
                                        itemRender={(current, type, originalElement) => {
                                            if (type === 'prev' || type === 'next') {
                                                return null;
                                            }
                                            return originalElement;
                                        }}
                                        className="custom-pagination"
                                    />
                                )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <button
                                    onClick={() => {
                                        if (page > 1) {
                                            handlePageChange(page - 1, pageSize);
                                        }
                                    }}
                                    disabled={page <= 1}
                                    style={{ 
                                        background: 'none',
                                        border: 'none',
                                        fontFamily: 'Inter', 
                                        fontSize: '12px', 
                                        fontWeight: 400, 
                                        lineHeight: '18px', 
                                        letterSpacing: '-0.1px', 
                                        color: page > 1 ? '#666' : '#ccc', 
                                        cursor: page > 1 ? 'pointer' : 'not-allowed', 
                                        padding: '0 16px 0 0'
                                    }}
                                >
                                    ← Previous
                                </button>
                                <div 
                                    style={{ 
                                        borderLeft: '1px solid #d9d9d9',
                                        margin: '0 8px',
                                        height: '20px'
                                    }}
                                />
                                <button
                                    onClick={() => {
                                        const maxPage = totalPages;
                                        if (page < maxPage) {
                                            handlePageChange(page + 1, pageSize);
                                        }
                                    }}
                                    disabled={page >= totalPages}
                                    style={{ 
                                        background: 'none',
                                        border: 'none',
                                        fontFamily: 'Inter', 
                                        fontSize: '12px', 
                                        fontWeight: 400, 
                                        lineHeight: '18px', 
                                        letterSpacing: '-0.1px', 
                                        color: page < totalPages ? '#666' : '#ccc', 
                                        cursor: page < totalPages ? 'pointer' : 'not-allowed',
                                        padding: '0 0 0 8px'
                                    }}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}
                />

                <QuestionFilters
                    open={filterDrawerOpen}
                    onClose={() => setFilterDrawerOpen(false)}
                    onApplyFilters={handleApplyFilters}
                />

                <Modal
                    title="Delete Question"
                    visible={deleteDialogOpen}
                    onCancel={() => setDeleteDialogOpen(false)}
                    onOk={handleDelete}
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ 
                        danger: true,
                        loading: isLoading 
                    }}
                >
                    <p>Are you sure you want to delete the question: <strong>{selectedQuestion?.question}</strong>? This action cannot be undone.</p>
                </Modal>

                <Modal
                    title="Enter Round Number"
                    visible={roundDialogOpen}
                    onCancel={() => setRoundDialogOpen(false)}
                    onOk={handleRoundSubmit}
                    okText="Add Questions"
                    cancelText="Cancel"
                >
                    <Input
                        type="number"
                        placeholder="Enter round number (e.g., 1, 2, 3)"
                        value={selectedRound}
                        onChange={(e) => setSelectedRound(e.target.value)}
                        min={1}
                    />
                </Modal>

                <AddQuestionDialog
                    open={addDialogOpen}
                    onClose={handleAddDialogClose}
                />

                <AddQuestionDialog
                    open={editDialogOpen}
                    onClose={handleEditDialogClose}
                    questionData={selectedQuestion}
                    isEdit={true}
                />

                <UploadQuestionDialog
                    open={uploadDialogOpen}
                    onClose={handleUploadDialogClose}
                />
            </div>
        </div>
    );
}

export default QuestionList; 